import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Knowing when a deploy has left this tab behind, and when it is safe to say so.
 *
 * Every build writes its id to /version.json (see vite.config.ts) and bakes the
 * same id into the bundle. A tab whose baked id is older than the served one is
 * running code that is no longer deployed, and gets asked to reload.
 *
 * The ask is a blocking modal whose only exit is a reload, so it must not land
 * in the middle of work a reload would throw away: an upload or submit still on
 * the wire, or an AI extraction the user is watching. Those make the guard busy,
 * and the prompt waits for them.
 *
 * Some of that work outlives the component that knows about it. A finished
 * extraction is the case: the run is over, but the user has not yet reached the
 * draft it produced, and reloading them off the completion screen loses the
 * thread. The screen that knows the run finished unmounts on the way to the
 * draft, so it cannot hold the prompt back itself — hence deferrals, which one
 * component opens by name and another closes.
 */

export const VERSION_PATH = '/version.json';

const BUILD_ID = /^\d+$/;

/** The build id out of a /version.json body, or null for anything else. */
export const parseBuildId = (body: unknown): string | null => {
  if (!body || typeof body !== 'object') return null;
  const id = (body as { buildId?: unknown }).buildId;
  return typeof id === 'string' && BUILD_ID.test(id) ? id : null;
};

/**
 * Build ids are the build's epoch milliseconds, so they order. Newer, not
 * merely different: during a rolling deploy old and new pods answer side by
 * side, and a tab that reloaded onto an old pod must not be told to "update"
 * back to the build it just left.
 */
export const isNewerBuild = (running: string, deployed: string | null): boolean =>
  deployed !== null && BUILD_ID.test(running) && Number(deployed) > Number(running);

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Longer than the 5-minute axios timeout, so a real request always settles
 * first. Only a write whose settle was never observed ages out, instead of
 * holding the prompt back for the life of the tab.
 */
export const STALE_WRITE_MS = 10 * 60 * 1000;

export const createUpdateGuard = (now: () => number = Date.now) => {
  const writes = new Map<number, number>();
  const holds = new Set<symbol>();
  const deferrals = new Map<string, number>();
  const listeners = new Set<() => void>();
  let nextWriteId = 1;

  const emit = () => {
    for (const listener of listeners) listener();
  };

  return {
    beginWrite(): number {
      const id = nextWriteId++;
      writes.set(id, now());
      emit();
      return id;
    },

    endWrite(id: number) {
      if (writes.delete(id)) emit();
    },

    /**
     * Keeps the prompt back until `clearDeferral(key)`, or until ttlMs has
     * passed — whichever comes first.
     *
     * The expiry is the point: a handover across a navigation is not
     * guaranteed to happen. A user who closes the extraction screen and goes
     * somewhere else entirely would otherwise hold the prompt back for the
     * life of the tab, which is worse than being asked to reload a little
     * early. Re-opening an existing key extends it rather than stacking.
     */
    defer(key: string, ttlMs: number) {
      deferrals.set(key, now() + ttlMs);
      emit();
    },

    clearDeferral(key: string) {
      if (deferrals.delete(key)) emit();
    },

    /** Keeps the prompt back until the returned release is called. */
    hold(): () => void {
      const token = Symbol('app-update-hold');
      holds.add(token);
      emit();
      return () => {
        if (holds.delete(token)) emit();
      };
    },

    isBusy(): boolean {
      if (holds.size > 0) return true;
      const at = now();
      for (const [key, expiry] of deferrals) {
        if (expiry > at) return true;
        deferrals.delete(key);
      }
      const cutoff = at - STALE_WRITE_MS;
      for (const [id, startedAt] of writes) {
        if (startedAt >= cutoff) return true;
        writes.delete(id);
      }
      return false;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type UpdateGuard = ReturnType<typeof createUpdateGuard>;

export const updateGuard = createUpdateGuard();

type TrackedConfig = InternalAxiosRequestConfig & { appUpdateWriteId?: number };

/**
 * Counts an instance's writes as busy while they are in flight.
 *
 * Register it after the instance's own interceptors: request interceptors run
 * last-registered-first, so the write is counted before anything else can
 * reject it. The id travels on the config, which axios copies into a retried
 * request (the 401 refresh path), so a retry neither counts twice nor drops the
 * original.
 */
export const trackPendingWrites = (instance: Pick<AxiosInstance, 'interceptors'>, guard: UpdateGuard = updateGuard) => {
  const settle = (config: TrackedConfig | undefined) => {
    if (config?.appUpdateWriteId === undefined) return;
    guard.endWrite(config.appUpdateWriteId);
    config.appUpdateWriteId = undefined;
  };

  instance.interceptors.request.use((config: TrackedConfig) => {
    if (config.appUpdateWriteId === undefined && WRITE_METHODS.has((config.method ?? 'get').toLowerCase())) {
      config.appUpdateWriteId = guard.beginWrite();
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      settle(response.config as TrackedConfig);
      return response;
    },
    (error) => {
      settle(error?.config);
      return Promise.reject(error);
    }
  );
};
